<template>
  <ion-page>
    <ion-content>
      <div class="filter-form">
        <ion-label>
          <h2 class="title">購入履歴</h2>
          <div class="form">
            <ion-radio-group v-model="currentFilterType">
              <div class="all">
                <ion-radio value="all" labelPlacement="end">すべて</ion-radio>
              </div>
              <div class="filter">
                <ion-radio value="period" labelPlacement="end"></ion-radio>
                <DatePicker id="filterFrom" v-model="periodFilterFrom"></DatePicker>
                〜
                <DatePicker id="filterTo" v-model="periodFilterTo"></DatePicker>
              </div>
            </ion-radio-group>
          </div>
          <p>件数: {{ paginateMeta?.total }} 件</p>
        </ion-label>
      </div>
      <div class="history">
        <template v-for="item in transactions" :key="item.id">
          <div :class="{ canceled : item.canceled_at }">
            <h3>{{ dateFormat(item.sales_at, 'yyyy年MM月dd日 HH時mm分') }} {{ item.store_name }}</h3>
            <ion-list>
            <template v-for="detail in item.details" :key="detail.id">
              <ion-item>
              <div class="item">
                <h4>ブランド名: {{ detail.item_brand_abbreviation }}</h4>
                <h4>商品名: {{ detail.item_name }}</h4>
                <div class="attributes">
                  <p>品番: {{ detail.item_part_number }}</p>
                  <p>サイズ: {{ detail.item_size }}  カラー: {{ detail.item_color }}</p>
                </div>
              </div>
              </ion-item>
            </template>
            </ion-list>
            <div class="total">
              <span>お買い上げ金額</span>
              <span class="amount">{{ item.total_price.toLocaleString('ja-JP') }}円<span class="tax-note">({{ inclusiveTax ? '税込' : '税抜' }})</span></span>
            </div>
            <div class="cancel" v-if="item.canceled_at">
              <span class="chip">返品済</span> <span class="date">返品日：{{ dateFormat(item.canceled_at, 'yyyy年MM月dd日') }}</span></div>
            </div>
        </template>
      </div>
      <ion-infinite-scroll @ionInfinite="onScrolledBottom">
        <ion-infinite-scroll-content></ion-infinite-scroll-content>
      </ion-infinite-scroll>
    </ion-content>
  </ion-page>

</template>

<script setup lang="ts">
import {
  IonContent,
  IonLabel,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonList,
  IonItem,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/vue'
import { ref, computed, onMounted, watch } from 'vue'
import { PaginateMeta } from "@/actions/paginate"
import { dateFormat } from "@/helpers/date-format"
import { listPosTransactions, PosTransaction } from "@/actions/list-pos-transactions"
import { InfiniteScrollCustomEvent } from '@ionic/vue'
import DatePicker from '@/components/DatePicker.vue'
const currentFilterType = ref<'all' | 'period'>('all')
const currentDate = new Date()
const periodFilterFrom = ref(dateFormat(new Date(currentDate.getFullYear(), currentDate.getMonth() -1, currentDate.getDate())))
const periodFilterTo = ref(new Date().toISOString())

const posTransactions = ref<PosTransaction[]>([])
const transactions = computed(() => {
  return posTransactions.value;
});
const paginateMeta = ref<PaginateMeta>()
const accumulatedAmount = ref<number>()
const rankAmount = ref<number>()
const rankAggregatePeriodFrom = ref<Date>()
const rankAggregatePeriodTo = ref<Date>()
const firstTransactionAt = ref<Date>()
const inclusiveTax = ref<boolean>()
onMounted(async () => {
  const trans = await fetchPosTransactions()
  posTransactions.value = trans.posTransactions
  paginateMeta.value = trans.paginateMeta
  accumulatedAmount.value = trans.accumulatedAmount
  rankAmount.value = trans.rankAmount
  rankAggregatePeriodFrom.value = trans.rankAggregatePeriodFrom
  rankAggregatePeriodTo.value = trans.rankAggregatePeriodTo
  firstTransactionAt.value = trans.firstTransactionAt
  inclusiveTax.value = trans.inclusiveTax
})

async function fetchPosTransactions(
    from: Date | null = null,
    to: Date | null = null,
    page: number = 0,
) {
  try {
    const response = await listPosTransactions({ from, to, page })
    return {
      posTransactions: response.data.posTransactions,
      paginateMeta: response.data.meta,
      accumulatedAmount: response.data.accumulatedAmount,
      rankAmount: response.data.rankAmount,
      rankAggregatePeriodFrom: response.data.rankAggregatePeriodFrom,
      rankAggregatePeriodTo: response.data.rankAggregatePeriodTo,
      firstTransactionAt: response.data.firstTransactionAt,
      inclusiveTax: response.data.inclusiveTax,
    }
  } catch (err) {
    throw new Error('購入履歴の取得に失敗しました。', { cause: err });
  }
}

/**
 * 下部までスクロールされたら発火される
 * @param ev
 */
async function onScrolledBottom(ev: InfiniteScrollCustomEvent): Promise<void> {
  const nextPage = paginateMeta.value?.current_page! + 1
  const from = new Date((document.querySelector('#filterFrom .aux-input') as HTMLInputElement).value)
  const to = new Date((document.querySelector('#filterTo .aux-input') as HTMLInputElement).value)
  const result = await fetchPosTransactions(from, to, nextPage)
  posTransactions.value = posTransactions.value.concat(result.posTransactions)
  paginateMeta.value = result.paginateMeta
  ev.target.complete()
}

async function doSearch() {
  let trans;
  if (currentFilterType.value === 'all') {
    trans = await fetchPosTransactions()
  } else {
    const from = new Date(periodFilterFrom.value)
    const to = new Date(periodFilterTo.value)
    trans = await fetchPosTransactions(from, to)
  }
  posTransactions.value = trans.posTransactions
  paginateMeta.value = trans.paginateMeta
}
watch(currentFilterType, async (newValue, oldValue) => {
  doSearch()
})
watch(periodFilterFrom, async (newValue, oldValue) => {
  doSearch()
})
watch(periodFilterTo, async (newValue, oldValue) => {
  doSearch()
})
</script>

<style scoped lang="scss">

ion-radio-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin:10px 0;
}

ion-radio  {
  margin-right: 5px;
  font-size:0.75rem !important;
}


ion-radio::part(container) {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 2px solid #ddd;
}

ion-radio::part(mark) {
  background: none;
  transition: none;
  transform: none;
  border-radius: 0;
}

ion-radio.radio-checked::part(container) {
  background: var(--ion-color-primary);
  border-color: transparent;
}

ion-radio.radio-checked::part(mark) {
  width: 6px;
  height: 10px;

  border-width: 0px 2px 2px 0px;
  border-style: solid;
  border-color: #fff;

  transform: rotate(45deg);

}

.all,.filter {
  margin-top: 5px;
  border-width: 1px;
  border-style: solid;
  border-color: #ccc;
  border-radius: 1rem;
  padding:0.25rem 0.3rem;
  margin-right:5px;

}

.filter {
  display: flex;
  align-items: center;
}

.filter-form {
  padding: 10px;
}

.form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  ion-label {
      font-size:0.75rem !important;
  }
}


ion-datetime-button {
  font-size:0.75rem;

}


.border {
  height: 10px;
  background-color: #ccc;
  margin: 10px 0;
}

.smart_receipt {
  text-align: center;
  margin: 10px;
}

.purchase-section {
  width: 95%;
  margin: 0 auto;
  padding: 10px;
}

.title {
  text-align: center;
  padding: 5px;
  margin-bottom:0.5rem;
  font-weight: bold;
}

.purchase-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 20px;
  margin-bottom: 10px;
  background-color: var(--ion-color-light);
  border-radius: 4px;
}

.purchase-block > div {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.purchase-block p {
  font-weight: bold;
  margin-bottom: 8px;
}

.purchase-block .period, .purchase-block .amount {
  margin: 0;
}

.purchase-block .period {
  font-size: 0.8em;
}

.purchase-block .amount {
  font-size: 1.2em;
  font-weight: bold;
}

.tax-note {
  font-size: 0.5em;
  font-weight: normal;
}

.note {
  font-size: 0.8em;
  margin-left: 20px;
}


.history h3 {
  font-size: 1rem;
  color: var(--ion-color-light);
  background: rgb(73, 73, 73);
  padding: 0.5rem 0.5rem;
  text-align: left;
    &::before {
    content:"";
    display:inline-block;
    height: 1.1em;
    vertical-align: text-bottom;
    margin-right: 6px;
    border-left:4px solid var(--ion-color-light);
  }

}
.history h4 {
  padding-left: 0.5em;
  margin-top: 0.75rem;
  font-weight: bold;
  font-size: 1rem;
}

.history ion-item:is(:last-child){
  --inner-border-width: 0px;
}

.history .attributes {
  margin: 0px 0 5px;
  padding-left: 1.5em;
  p {
    margin: 0;
    font-size: 0.8em;
  }
}
.history {
  .item {
    padding-bottom:0.5rem;
  }

  .total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding:0.5rem 1.5rem;
    margin-top: 0.5rem;
    border-top: 2px solid rgb(64, 64, 64);
  }
  .amount {
    font-size: 1.5em;
    font-weight: bold;
  }
  .tax {
    font-size: 0.6em;
    font-weight: normal;
  }
  .cancel {
    border-top: 1px solid #494949;
    border-bottom: 2px solid #494949;
    // background-color: red;
    font-size: 15px;
    padding: 0.5rem 1.5rem;

    display: flex;
    justify-content: space-between;
    align-items: center;

    .chip {
      background-color: #494949;
      color: white;
      padding: 0.25rem 0.5rem;
      border-radius: 0.1rem;
    }
    .date {
      margin-left: 0.5rem;
      color:#494949;
    }
  }

  .canceled {
    h3 {
      background-color: #494949;
    }
    .total {
      border-color: #494949;
        .amount {
          color:#777;

        }
        .tax {
             color:#777;
        }
    }
  }

}
</style>
